import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link, useSearchParams, useParams, useNavigate } from "react-router-dom";
import { Search, ChevronRight, ExternalLink, RefreshCw, Play, Share2, Newspaper, ChevronDown, ChevronUp } from "lucide-react";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { SEO, breadcrumbSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FALLBACK_IMAGES = [
    "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1540747737956-378724044402?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1518063319789-7217e6706b04?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1577223625816-7546f13df25d?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1504305754058-2f08ccd89a0a?q=80&w=600&auto=format&fit=crop",
    "https://images.unsplash.com/photo-1431324155629-1a6edd1d141d?q=80&w=600&auto=format&fit=crop",
];

const getCategoryForArticle = (item) => {
    const title = item.title.toLowerCase();
    if (title.includes("ligue 1") || title.includes("psg") || title.includes("om") || title.includes("classique")) return "Ligue 1";
    if (title.includes("ldc") || title.includes("champions league") || title.includes("europe")) return "Ligue des Champions";
    if (title.includes("mercato") || title.includes("transfert") || title.includes("recrute")) return "Mercato";
    if (title.includes("deschamps") || title.includes("bleus") || title.includes("edf")) return "Équipe de France";
    if (item.source) return item.source;
    return "Football";
};

const CATEGORIES = [
    { name: "Tous", slug: "tous" },
    { name: "Analyses", slug: "analyse" },
    { name: "Transferts", slug: "transfert" },
    { name: "En Direct", slug: "direct" },
    { name: "Pronostics", slug: "pronostic" },
    { name: "Classements", slug: "classement" },
    { name: "Interviews", slug: "interview" },
];

const Actus = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { categorySlug } = useParams();
    const navigate = useNavigate();
    const page = parseInt(searchParams.get("page") || "1", 10);
    const limit = 12;

    const [q, setQ] = useState("");
    const [rss, setRss] = useState([]);
    const [rssLoading, setRssLoading] = useState(true);
    const [source, setSource] = useState("Toutes");
    const [ownArticles, setOwnArticles] = useState([]);
    const [ownArticlesLoading, setOwnArticlesLoading] = useState(true);
    const [livescoreNews, setLivescoreNews] = useState({ articles: [], videos: [] });
    const [livescoreLoading, setLivescoreLoading] = useState(true);
    const [totalArticlesCount, setTotalArticlesCount] = useState(0);

    const activeCategory = useMemo(() => {
        if (!categorySlug) return "Tous";
        const found = CATEGORIES.find(c => c.slug === categorySlug.toLowerCase());
        return found ? found.name : "Tous";
    }, [categorySlug]);

    useEffect(() => {
        let cancelled = false;
        axios
            .get(`${API}/rss?limit=40`)
            .then(({ data }) => {
                if (!cancelled) {
                    setRss(data);
                    setRssLoading(false);
                }
            })
            .catch(() => !cancelled && setRssLoading(false));

        axios
            .get(`${API}/livescore-news`)
            .then(({ data }) => {
                if (!cancelled) {
                    setLivescoreNews(data);
                    setLivescoreLoading(false);
                }
            })
            .catch(() => !cancelled && setLivescoreLoading(false));

        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        let cancelled = false;
        setOwnArticlesLoading(true);

        const catParam = activeCategory !== "Tous" ? CATEGORIES.find(c => c.name === activeCategory)?.slug : "";
        const catQuery = catParam ? `&categorie=${catParam}` : "";
        const url = q ? `${API}/articles` : `${API}/articles?page=${page}&limit=${limit}${catQuery}`;

        axios
            .get(url)
            .then((res) => {
                if (!cancelled) {
                    setOwnArticles(res.data);
                    const total = parseInt(res.headers["x-total-count"] || res.data.length.toString(), 10);
                    setTotalArticlesCount(total);
                    setOwnArticlesLoading(false);
                }
            })
            .catch(() => {
                if (!cancelled) {
                    setOwnArticlesLoading(false);
                }
            });

        return () => {
            cancelled = true;
        };
    }, [page, q, activeCategory]);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, [page]);


    const availableSources = useMemo(() => {
        const set = new Set(rss.map((r) => r.source));
        return ["Toutes", ...Array.from(set)];
    }, [rss]);

    const filteredRss = useMemo(() => {
        return rss.filter((r) => {
            if (!r.image || r.image.trim() === "") return false;
            const matchSource = source === "Toutes" || r.source === source;
            return matchSource;
        });
    }, [rss, source]);

    const filteredOwnArticles = useMemo(() => {
        const needle = q.toLowerCase();
        return ownArticles.filter((a) => {
            const matchQ =
                !needle ||
                a.title.toLowerCase().includes(needle) ||
                a.excerpt.toLowerCase().includes(needle) ||
                (a.content && a.content.toLowerCase().includes(needle));
            return matchQ;
        });
    }, [ownArticles, q]);

    const isFirstPage = page === 1 && !q;
    const featuredArticle = isFirstPage ? filteredOwnArticles[0] : null;
    const gridArticles = isFirstPage ? filteredOwnArticles.slice(1) : filteredOwnArticles;

    const totalPages = Math.ceil(totalArticlesCount / limit);
    const showPagination = !q && totalPages > 1;

    const baseUrl = categorySlug
        ? `https://pronospot.com/actualites/categorie/${categorySlug.toLowerCase()}`
        : "https://pronospot.com/";

    const canonicalUrl = !q && page > 1 ? `${baseUrl}?page=${page}` : baseUrl;
    const prevUrl = !q && page > 1 ? (page === 2 ? baseUrl : `${baseUrl}?page=${page - 1}`) : null;
    const nextUrl = !q && page < totalPages ? `${baseUrl}?page=${page + 1}` : null;

    const breadcrumbItems = useMemo(() => {
        const items = [{ name: "Accueil", url: "https://pronospot.com/" }];
        if (categorySlug) {
            items.push({ name: "Actualités", url: "https://pronospot.com/" });
            items.push({ name: activeCategory, url: baseUrl });
        } else {
            items.push({ name: "Actualités", url: "https://pronospot.com/actus" });
        }
        return items;
    }, [categorySlug, activeCategory, baseUrl]);

    return (
        <div data-testid="actus-page" className="bg-[#07111D] min-h-screen text-white">
            <SEO
                title={categorySlug ? `${activeCategory} Foot | Analyses et Pronostics | Pronospot.com` : "Actualités, Analyses et Pronostics Foot | Pronospot.com"}
                description={categorySlug ? `Retrouvez toute l'actualité et les analyses football classées dans la catégorie ${activeCategory}.` : "Toute l'actualité football en France : analyses des matchs, transferts, pronostics et actus des bookmakers."}
                canonical={canonicalUrl}
                prev={prevUrl}
                next={nextUrl}
                jsonLd={breadcrumbSchema(breadcrumbItems)}
            />

            {/* BREAKING NEWS MARQUEE */}
            <BreakingNewsTicker articles={rss} />

            <section className="container-x pt-8 pb-6">
                <Breadcrumbs items={[{ label: "Actualités" }]} />
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mt-3">
                    <div>
                        <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white tracking-tighter">
                            Actualités Foot
                        </h1>
                        <p className="text-gray-400 mt-2 max-w-2xl text-sm sm:text-base">
                            Analyses de matchs, pronostics d'experts, transferts chauds et informations exclusives.
                        </p>
                    </div>
                </div>
            </section>

            {/* Search and Filter Row */}
            <section className="container-x mb-8">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-[#0D1B2A]/40 p-4 rounded-xl border border-[#2A4A6B]/40">
                    {availableSources.length > 1 && (
                        <div className="flex flex-wrap gap-2" data-testid="source-filter">
                            {availableSources.map((s) => (
                                <button
                                    key={s}
                                    onClick={() => setSource(s)}
                                    data-testid={`source-${s.toLowerCase().replace(/'/g, "").replace(/\s+/g, "-")}`}
                                    className={`text-xs px-3.5 py-1.5 rounded-full border transition-all ${source === s
                                            ? "bg-[#00C853] text-black border-[#00C853] font-semibold"
                                            : "bg-transparent text-gray-300 border-[#2A4A6B] hover:border-[#00C853]"
                                        }`}
                                >
                                    {s}
                                    {s !== "Toutes" && (
                                        <span className="ml-1 text-[9px] opacity-70">
                                            {rss.filter((r) => r.source === s).length}
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                    <div className="relative w-full md:w-80">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
                        />
                        <input
                            type="text"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Rechercher un article..."
                            className="w-full bg-[#0D1B2A] border border-[#2A4A6B] rounded-lg pl-9 pr-4 py-2 text-xs text-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-[#00C853]"
                        />
                    </div>
                </div>
            </section>

            {/* Category Filters for Articles */}
            <section className="container-x mb-6">
                <div className="flex flex-wrap items-center gap-2 bg-[#0D1B2A]/20 p-3 rounded-xl border border-[#2A4A6B]/20">
                    <span className="text-xs font-bold text-gray-400 mr-2">Catégories :</span>
                    {CATEGORIES.map((c) => {
                        const isActive = activeCategory === c.name;
                        return (
                            <button
                                key={c.slug}
                                onClick={() => {
                                    if (c.slug === "tous") {
                                        navigate("/");
                                    } else {
                                        navigate(`/actualites/categorie/${c.slug}`);
                                    }
                                }}
                                className={`text-xs px-3.5 py-1.5 rounded-lg border transition-all ${
                                    isActive
                                        ? "bg-[#00C853] text-black border-[#00C853] font-semibold"
                                        : "bg-transparent text-gray-300 border-[#2A4A6B]/60 hover:border-[#00C853]"
                                }`}
                            >
                                {c.name}
                            </button>
                        );
                    })}
                </div>
            </section>

            {/* MAIN TWO-COLUMN CONTAINER */}
            <section className="container-x pb-20" data-testid="rss-feed-section">
                {ownArticlesLoading ? (
                    <div className="flex items-center justify-center gap-2 py-20 text-gray-400 text-sm">
                        <RefreshCw size={18} className="animate-spin" />
                        Chargement des dernières analyses...
                    </div>
                ) : filteredOwnArticles.length === 0 ? (
                    <div className="card-elev p-12 text-center text-gray-400 text-sm">
                        Aucun article ne correspond à votre recherche.
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                        {/* LEFT COLUMN: Hero + Grid of Articles (2/3 width) */}
                        <div className="lg:col-span-2 space-y-8">

                            {/* 1. FEATURED HERO ARTICLE */}
                            {featuredArticle && (
                                <Link
                                    to={`/articles/${featuredArticle.slug}`}
                                    className="block card-elev p-0 overflow-hidden group border border-[#2A4A6B]/50 transition-all hover:border-[#00C853]/40"
                                    data-testid="rss-card-0"
                                >
                                    <div className="relative aspect-[16/9] md:aspect-[21/9] w-full overflow-hidden bg-black/30">
                                        <img
                                            src={featuredArticle.image || FALLBACK_IMAGES[0]}
                                            alt={featuredArticle.title}
                                            fetchPriority="high"
                                            className="w-full h-full object-cover group-hover:scale-102 transition-transform duration-500 opacity-80"
                                        />
                                        <div className="absolute inset-0 bg-gradient-to-t from-[#07111D] via-[#07111D]/30 to-transparent" />

                                        <div className="absolute bottom-4 left-4 right-4 md:bottom-6 md:left-6 md:right-6">
                                            <div className="flex items-center gap-3 mb-2 md:mb-3">
                                                <span
                                                    className="px-2 py-0.5 rounded text-[10px] font-black uppercase tracking-wider text-black bg-[#E63946]"
                                                >
                                                    {featuredArticle.category}
                                                </span>
                                                <span className="text-[10px] text-gray-300 font-bold uppercase tracking-wider">
                                                    {featuredArticle.author}
                                                </span>
                                                <span className="text-gray-400 select-none">•</span>
                                                <span className="text-[10px] text-gray-400 font-medium">
                                                    {featuredArticle.date}
                                                </span>
                                            </div>
                                            <h2 className="font-heading font-extrabold text-white text-xl sm:text-2xl md:text-3xl leading-tight group-hover:text-[#00C853] transition-colors">
                                                {featuredArticle.title}
                                            </h2>
                                            {featuredArticle.excerpt && (
                                                <p className="text-xs sm:text-sm text-gray-300 mt-2 leading-relaxed line-clamp-2">
                                                    {featuredArticle.excerpt}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            )}

                            {/* 2. SUB-GRID OF OTHER ARTICLES */}
                            {gridArticles.length > 0 && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    {gridArticles.map((a, i) => (
                                        <Link
                                            key={a.slug + i}
                                            to={`/articles/${a.slug}`}
                                            className="card-elev p-0 overflow-hidden flex flex-col group border border-[#2A4A6B]/30 hover:border-[#00C853]/40"
                                            data-testid={`rss-card-${i + 1}`}
                                        >
                                            <div className="relative aspect-[16/10] w-full overflow-hidden bg-black/20">
                                                <img
                                                    src={a.image || FALLBACK_IMAGES[(i + 1) % FALLBACK_IMAGES.length]}
                                                    alt={a.title}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                                                />
                                                <span
                                                    className="absolute top-3 left-3 px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-wider text-white border bg-[#00C853]/80 border-[#00C853]"
                                                >
                                                    {a.category}
                                                </span>
                                            </div>

                                            <div className="p-5 flex-1 flex flex-col justify-between">
                                                <div>
                                                    <div className="flex items-center justify-between text-[10px] text-gray-400 mb-2 font-bold uppercase tracking-wider">
                                                        <span>{a.author}</span>
                                                        <span>{a.date}</span>
                                                    </div>
                                                    <h3 className="font-heading font-bold text-white text-base leading-snug mb-2 group-hover:text-[#00C853] transition-colors line-clamp-3">
                                                        {a.title}
                                                    </h3>
                                                    {a.excerpt && (
                                                        <p className="text-xs text-gray-400 leading-relaxed line-clamp-2">
                                                            {a.excerpt}
                                                        </p>
                                                    )}
                                                </div>

                                                <div className="mt-4 pt-3 border-t border-[#2A4A6B]/50 text-[#00C853] text-xs font-semibold inline-flex items-center gap-1 group-hover:gap-2 transition-all">
                                                    Lire l'article
                                                    <ChevronRight size={12} />
                                                </div>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            )}

                            {/* PAGINATION CONTROLS */}
                            {showPagination && (
                                <div className="flex justify-center items-center gap-2 pt-8 border-t border-[#2A4A6B]/30" data-testid="pagination-controls">
                                    <button
                                        disabled={page <= 1}
                                        onClick={() => setSearchParams({ page: page - 1 })}
                                        className="px-4 py-2 rounded-lg bg-[#0D1B2A] border border-[#2A4A6B]/60 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-gray-300 hover:text-white hover:border-[#00C853] transition-all"
                                    >
                                        Précédent
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                                        <button
                                            key={p}
                                            onClick={() => setSearchParams({ page: p })}
                                            className={`px-3.5 py-2 rounded-lg text-xs font-bold transition-all ${
                                                p === page
                                                    ? "bg-[#00C853] text-black border border-[#00C853]"
                                                    : "bg-[#0D1B2A] border border-[#2A4A6B]/60 text-gray-300 hover:text-white hover:border-[#00C853]"
                                            }`}
                                        >
                                            {p}
                                        </button>
                                    ))}
                                    <button
                                        disabled={page >= totalPages}
                                        onClick={() => setSearchParams({ page: page + 1 })}
                                        className="px-4 py-2 rounded-lg bg-[#0D1B2A] border border-[#2A4A6B]/60 disabled:opacity-30 disabled:cursor-not-allowed text-xs font-bold text-gray-300 hover:text-white hover:border-[#00C853] transition-all"
                                    >
                                        Suivant
                                    </button>
                                </div>
                            )}

                        </div>

                        {/* RIGHT COLUMN: Sidebar (1/3 width) */}
                        <div className="space-y-6">

                            {/* 0. RSS FLASH FOOTBALL */}
                            <RssFlashWidget rss={filteredRss} loading={rssLoading} />

                            {/* 1. LIVE SCORES WIDGET */}
                            <LiveScoresWidget />

                            {/* 2. TOP STORIES WIDGET */}
                            <TopStoriesWidget articles={livescoreNews.articles} loading={livescoreLoading} />

                            {/* 3. FEATURED VIDEOS WIDGET */}
                            <FeaturedVideosWidget videos={livescoreNews.videos} loading={livescoreLoading} />

                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

const BreakingNewsTicker = ({ articles }) => {
    const tickerItems = useMemo(() => {
        if (!articles || articles.length === 0) {
            return ["BREAKING NEWS: DERNIERS PRONOSTICS EN DIRECT SUR PRONOSPOT !", "LIGUE 1 MCDONALD'S : LE PSG PREND LA TETE DU CHAMPIONNAT"];
        }
        return articles.slice(0, 5).map(a => `BREAKING NEWS: ${a.title.toUpperCase()}`);
    }, [articles]);

    return (
        <div className="w-full bg-[#DC2626] text-white flex items-center select-none overflow-hidden h-9 border-b border-[#2A4A6B]/20">
            <div className="bg-[#B3001E] px-4 py-2 text-xs font-black italic tracking-widest flex-shrink-0 uppercase z-10 shadow-lg flex items-center gap-1.5">
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" />
                Breaking News
            </div>
            <div className="flex-1 overflow-hidden relative flex items-center">
                <div 
                    className="animate-marquee flex items-center whitespace-nowrap gap-1"
                    style={{ 
                        animationDuration: `${Math.max(20, tickerItems.length * 8)}s` 
                    }}
                >
                    <div className="flex items-center shrink-0 text-xs font-extrabold uppercase tracking-wide">
                        {tickerItems.map((item, idx) => (
                            <span key={idx} className="mx-8 flex items-center">
                                {item}
                            </span>
                        ))}
                    </div>
                    <div className="flex items-center shrink-0 text-xs font-extrabold uppercase tracking-wide">
                        {tickerItems.map((item, idx) => (
                            <span key={idx} className="mx-8 flex items-center">
                                {item}
                            </span>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

const LiveScoresWidget = () => {
    const [scores, setScores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    const fetchScores = async () => {
        try {
            const res = await axios.get(`${API}/livescores`);
            if (res.data) {
                setScores(res.data);
            }
            setError(false);
        } catch (err) {
            console.error("Error fetching live scores:", err);
            setError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchScores();
        const interval = setInterval(fetchScores, 60000); // refresh every 60s
        return () => clearInterval(interval);
    }, []);

    return (
        <div className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B]">
            <div className="flex items-center justify-between mb-4 border-b border-[#2A4A6B] pb-2">
                <h3 className="font-heading font-extrabold text-white text-sm tracking-wider uppercase flex items-center gap-2">
                    <span className="w-2.5 h-2.5 rounded-full bg-[#E63946] animate-pulse" />
                    LIVE SCORES
                </h3>
                <button 
                    onClick={() => { setLoading(true); fetchScores(); }}
                    className="text-gray-400 hover:text-white transition-colors text-[10px] uppercase tracking-widest font-bold flex items-center gap-1"
                >
                    <RefreshCw size={10} className={loading ? "animate-spin" : ""} />
                    Actualiser
                </button>
            </div>

            {loading && scores.length === 0 ? (
                <div className="text-center text-xs text-gray-400 py-6">Chargement...</div>
            ) : error && scores.length === 0 ? (
                <div className="text-center text-xs text-red-400 py-6">Erreur de chargement</div>
            ) : scores.length === 0 ? (
                <div className="text-center text-xs text-gray-400 py-6">Aucun match aujourd'hui</div>
            ) : (
                <div className="space-y-3.5">
                    {scores.slice(0, 5).map(s => {
                        const isLive = s.status === "LIVE";
                        const isFinished = s.status === "FINISHED";
                        const isScheduled = s.status === "SCHEDULED";
                        
                        return (
                            <div key={s.id || `${s.teamA}-${s.teamB}`} className="bg-[#162236]/60 p-3 rounded-lg border border-[#2A4A6B]/50 flex flex-col gap-2 transition-all hover:border-[#00C853]/40">
                                <div className="flex justify-between items-center text-[9px] font-bold text-gray-400 uppercase tracking-wider">
                                    <span>{s.league}</span>
                                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-black ${
                                        isLive 
                                            ? "bg-[#E63946]/10 text-[#E63946] animate-pulse border border-[#E63946]/20" 
                                            : isFinished 
                                                ? "bg-gray-800 text-gray-400" 
                                                : "bg-[#00C853]/10 text-[#00C853] border border-[#00C853]/20"
                                    }`}>
                                        {isLive ? `DIRECT · ${s.time}` : isFinished ? "TERMINE" : s.time}
                                    </span>
                                </div>
                                <div className="flex items-center justify-between text-xs sm:text-sm">
                                    {/* Team A */}
                                    <div className="flex items-center gap-2 flex-1 min-w-0">
                                        {s.logoA && (
                                            <img src={s.logoA} alt={s.teamA} className="w-5 h-5 object-contain flex-shrink-0" loading="lazy" />
                                        )}
                                        <span className="font-heading font-bold text-white truncate">{s.teamA}</span>
                                    </div>

                                    {/* Score */}
                                    <div className="font-heading font-black text-[#00C853] bg-[#091321] px-2.5 py-1 rounded text-xs tabular-nums mx-3 shadow-inner border border-[#2A4A6B]/30 min-w-[45px] text-center">
                                        {isScheduled ? "VS" : `${s.scoreA} - ${s.scoreB}`}
                                    </div>

                                    {/* Team B */}
                                    <div className="flex items-center gap-2 flex-1 justify-end min-w-0 text-right">
                                        <span className="font-heading font-bold text-white truncate">{s.teamB}</span>
                                        {s.logoB && (
                                            <img src={s.logoB} alt={s.teamB} className="w-5 h-5 object-contain flex-shrink-0" loading="lazy" />
                                        )}
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
};

const TopStoriesWidget = ({ articles, loading }) => {
    if (loading) {
        return (
            <div className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B] text-center text-xs text-gray-400">
                Chargement...
            </div>
        );
    }
    const displayArticles = articles && articles.length > 0 ? articles.slice(0, 5) : [];

    return (
        <div className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B]">
            <h3 className="font-heading font-extrabold text-white text-sm tracking-wider uppercase border-b border-[#2A4A6B] pb-2 mb-4">
                TOP STORIES
            </h3>
            {displayArticles.length === 0 ? (
                <p className="text-xs text-gray-400">Aucune actualité disponible.</p>
            ) : (
                <ul className="space-y-3">
                    {displayArticles.map((a, idx) => (
                        <li key={idx} className="border-b border-[#2A4A6B]/30 pb-2.5 last:border-0 last:pb-0">
                            <a
                                href={a.url || a.link}
                                target="_blank"
                                rel="noopener noreferrer nofollow"
                                className="text-xs text-gray-300 hover:text-[#00C853] font-medium leading-relaxed block transition-colors"
                            >
                                • {a.title}
                            </a>
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
};

const FeaturedVideosWidget = ({ videos, loading }) => {
    if (loading) {
        return (
            <div className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B] text-center text-xs text-gray-400">
                Chargement des vidéos...
            </div>
        );
    }
    const displayVideos = videos && videos.length > 0 ? videos.slice(0, 5) : [];

    return (
        <div className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B]">
            <h3 className="font-heading font-extrabold text-white text-sm tracking-wider uppercase border-b border-[#2A4A6B] pb-2 mb-4">
                ANALYSES & VIDÉOS
            </h3>
            {displayVideos.length === 0 ? (
                <p className="text-xs text-gray-400">Aucune vidéo disponible.</p>
            ) : (
                <div className="space-y-4">
                    {displayVideos.map(v => (
                        <a
                            key={v.id}
                            href={`https://www.youtube.com/watch?v=${v.video_id}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="group flex gap-3 items-start cursor-pointer hover:bg-[#162236]/30 p-2 rounded-lg transition-colors"
                        >
                            <div className="relative w-24 h-16 rounded overflow-hidden flex-shrink-0 border border-[#2A4A6B]">
                                <img src={v.thumbnail || "https://images.unsplash.com/photo-1508098682722-e99c43a406b2?w=300"} alt={v.title} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200" />
                                <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                                    <Play size={16} className="text-white fill-white group-hover:scale-125 transition-transform animate-pulse" />
                                </div>
                            </div>
                            <div className="flex-1 min-w-0">
                                <h4 className="font-heading font-bold text-xs text-white leading-snug group-hover:text-[#00C853] transition-colors line-clamp-2">
                                    {v.title}
                                </h4>
                                <span className="text-[10px] text-gray-400 mt-1 block">Livescore Highlights</span>
                            </div>
                        </a>
                    ))}
                </div>
            )}
        </div>
    );
};

const RssFlashWidget = ({ rss, loading }) => {
    const [expanded, setExpanded] = useState(false);
    
    if (loading) {
        return (
            <div className="card-elev p-6 bg-[#0D1B2A]/90 border border-[#2A4A6B] flex flex-col items-center justify-center gap-3 py-12 text-gray-400 text-sm">
                <RefreshCw className="animate-spin text-[#00C853]" size={20} />
                <span>Chargement du flash info...</span>
            </div>
        );
    }

    if (!rss || rss.length === 0) {
        return null;
    }

    const displayedItems = expanded ? rss : rss.slice(0, 8);
    const hasMore = rss.length > 8;

    return (
        <div 
            data-testid="bulletin-flash-widget"
            className="card-elev p-5 bg-[#0D1B2A]/90 border border-[#2A4A6B] relative overflow-hidden transition-all duration-300 hover:border-[#00C853]/60"
        >
            {/* Top pulsing flash badge */}
            <div className="flex items-center justify-between mb-4 border-b border-[#2A4A6B]/50 pb-3">
                <div className="flex items-center gap-2">
                    <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#E63946] opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-[#E63946]"></span>
                    </span>
                    <h3 className="font-heading font-extrabold text-white text-xs tracking-wider uppercase flex items-center gap-1.5">
                        <Newspaper size={13} className="text-[#00C853]" />
                        FLASH FOOTBALL
                    </h3>
                </div>
            </div>

            {/* RSS Items */}
            <div className="space-y-3.5">
                {displayedItems.map((item, index) => (
                    <a
                        key={index}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer nofollow"
                        className="group flex gap-3 items-start cursor-pointer hover:bg-[#162236]/30 p-2 rounded-lg transition-all duration-200 border border-transparent hover:border-[#2A4A6B]/30"
                    >
                        {/* Image sans badge de source */}
                        <div className="relative w-24 h-16 rounded overflow-hidden flex-shrink-0 border border-[#2A4A6B]/50 bg-black/10">
                            <img 
                                src={item.image || FALLBACK_IMAGES[index % FALLBACK_IMAGES.length]} 
                                alt={item.title} 
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200" 
                                loading="lazy"
                            />
                        </div>

                        <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-[10px] text-[#00C853] font-bold uppercase tracking-wider">
                                    {item.source}
                                </span>
                                <span className="text-[9px] text-gray-500 font-mono">
                                    {item.published}
                                </span>
                            </div>
                            <h4 className="font-heading font-bold text-xs text-white leading-snug group-hover:text-[#00C853] transition-colors line-clamp-2">
                                {item.title}
                            </h4>
                        </div>
                    </a>
                ))}
            </div>

            {/* Expand / Collapse Button */}
            {hasMore && (
                <button
                    onClick={() => setExpanded(!expanded)}
                    className="w-full mt-4 py-2 border border-[#2A4A6B]/40 hover:border-[#00C853]/30 bg-transparent hover:bg-[#1E3A5F]/20 rounded-lg text-xs font-bold text-gray-300 hover:text-white flex items-center justify-center gap-1.5 transition-all"
                >
                    {expanded ? (
                        <>
                            Voir moins <ChevronUp size={14} />
                        </>
                    ) : (
                        <>
                            Voir tout ({rss.length}) <ChevronDown size={14} />
                        </>
                    )}
                </button>
            )}
        </div>
    );
};

export default Actus;
